import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import {
  rentPaymentFieldConfiguration,
  rentPaymentInitialValues,
} from "../../../formConfigs/Fleet/rentPaymentConfig";
import { useMsal } from "@azure/msal-react";

export const fetchRents = async (token: string | null | undefined) => {
  const response = await axios.get(`${apiVariable}/getAllRents`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return response.data.map((rent: any) => ({
    label: `${rent.billToCompanyName} - ${rent.unitNumber} - $${rent.baseRentalRate}`,
    id: rent.id.toString(),
  }));
};

const RentPaymentPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { accounts } = useMsal();
  const userName = accounts[0].name;

  const {
    data: rentsData,
    isError,
    error,
  } = useQuery({
    queryKey: ["rentsFormated"],
    queryFn: () => fetchRents(token),
    enabled: !!open,
  });

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage((error as any).response?.data || "Failed to fetch rents");
    }
  }, [isError, error]);

  const rentPaymentMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/rentPayment`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["rentPayments"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit rent payment");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      rentId: getId(data.rentId),
      createdBy: userName,
    };
    rentPaymentMutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Create Rent Payment
      </Button>
      <GenericFormPopup
        formFields={rentPaymentFieldConfiguration}
        initialValues={rentPaymentInitialValues}
        title="Rent Payment Form"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={{ rents: rentsData }}
        loading={rentPaymentMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Rent Payment added successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default RentPaymentPopup;
